import type { AuditUpdateRequest } from '../../../../services/auditService';
import { normalizeTimePart } from './nonTicketReportDateUtils';

const VENDING_SALES_END_OF_DAY_TIME = '23:59:59';

const firstNonEmptyString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }

  return '';
};

const firstNormalizedTime = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = normalizeTimePart(value);
    if (normalized) return normalized;
  }

  return '';
};

const toOptionalNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toRequiredNumberOrZero = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toVendingSalesCount = (value: unknown, amount: unknown): number | null => {
  const parsed = toOptionalNumberOrNull(value);
  if (parsed !== null) {
    return parsed;
  }

  return toRequiredNumberOrZero(amount) === 0 ? 0 : null;
};

const normalizeSelectedPacks = (line: any): Array<{ pack_id: number | null; remaining_qty: number | null }> => {
  const selectedPacks = Array.isArray(line?.selected_packs) ? line.selected_packs : [];
  const requiredPackCount = toOptionalNumberOrNull(line?.required_pack_count) ?? 0;
  const slotCount = Math.max(requiredPackCount, selectedPacks.length, 1);

  return Array.from({ length: slotCount }, (_, index) => {
    const packEntry = selectedPacks[index] || {};
    return {
      pack_id: toOptionalNumberOrNull(packEntry?.pack_id ?? packEntry?.selected_pack_id ?? packEntry?.pack),
      remaining_qty: toOptionalNumberOrNull(packEntry?.remaining_qty ?? packEntry?.quantity),
    };
  });
};

const getPrimarySelectedPackId = (line: any): number | null => {
  const selectedPacks = normalizeSelectedPacks(line);
  const selectedPack = selectedPacks.find((pack) => pack.pack_id !== null && pack.pack_id !== undefined);
  return selectedPack?.pack_id ?? toOptionalNumberOrNull(line?.selected_pack_id ?? line?.pack_id ?? line?.pack);
};

const getPackCapacity = (line: any): number | null => toOptionalNumberOrNull(line?.pack_capacity);

const hasMeaningfulVendingSalesLine = (line: any): boolean => (
  line?.game !== null
  || line?.count !== null
  || toRequiredNumberOrZero(line?.amount) !== 0
);

const hasMeaningfulVendingInventoryLine = (line: any): boolean => (
  line?.game !== null
  || line?.quantity !== null
  || toRequiredNumberOrZero(line?.value) !== 0
  || line?.selected_pack_id !== null
  || (Array.isArray(line?.selected_packs) && line.selected_packs.some((pack: any) => pack?.pack_id !== null))
);

export const buildNonTicketReportUpdateRequest = (
  reportType: string,
  data: any,
  queueId?: string,
): AuditUpdateRequest => {
  if (reportType === 'vending_shift_report') {
    const parsedData = data?.parsed_data ?? {};
    const finalJson = parsedData?.final_json ?? {};
    const nestedShiftFinalJson = { ...finalJson };
    delete nestedShiftFinalJson.lines;
    delete nestedShiftFinalJson.scratcher_lines;
    delete nestedShiftFinalJson.draw_games;
    const salesLines = Array.isArray(finalJson?.sales?.lines)
      ? finalJson.sales.lines
      : (Array.isArray(finalJson?.sales?.scratcher_lines) ? finalJson.sales.scratcher_lines : []);
    const inventoryLines = Array.isArray(finalJson?.inventory?.lines) ? finalJson.inventory.lines : [];
    const shiftDate = firstNonEmptyString(data?.shift_date, parsedData.shift_date, finalJson.shift_date, finalJson.report_date);
    const shiftTime = firstNormalizedTime(
      data?.shift_start_time,
      data?.report_time,
      parsedData.report_time,
      parsedData.shift_time,
      finalJson.to_datetime,
      finalJson.report_time,
      finalJson.shift_time,
      finalJson.time,
    );
    const locationKey = firstNonEmptyString(data?.location_key, parsedData.location_key, finalJson.location_key) || 'VM-01';
    const includeInventoryReport = Boolean(data?.include_inventory_report);

    const normalizedSalesLines = salesLines
      .map((line: any) => ({
        bin: toOptionalNumberOrNull(line?.bin),
        game: toOptionalNumberOrNull(line?.game),
        count: toVendingSalesCount(line?.count, line?.amount),
        amount: toRequiredNumberOrZero(line?.amount),
      }))
      .filter(hasMeaningfulVendingSalesLine);
    const normalizedInventoryLines = inventoryLines
      .map((line: any) => ({
        bin: toOptionalNumberOrNull(line?.bin),
        game: toOptionalNumberOrNull(line?.game),
        price: toOptionalNumberOrNull(line?.price ?? line?.ppt),
        quantity: toOptionalNumberOrNull(line?.quantity ?? line?.count),
        value: toRequiredNumberOrZero(line?.value ?? line?.amount),
        selected_pack_id: getPrimarySelectedPackId(line),
        required_pack_count: toOptionalNumberOrNull(line?.required_pack_count),
        pack_capacity: getPackCapacity(line),
        selected_packs: normalizeSelectedPacks(line),
      }))
      .filter(hasMeaningfulVendingInventoryLine);
    const normalizedDrawGames = Array.isArray(finalJson?.sales?.draw_games)
      ? finalJson.sales.draw_games.map((line: any) => ({
        ...line,
        count: toVendingSalesCount(line?.count, line?.amount),
        amount: toRequiredNumberOrZero(line?.amount),
      }))
      : [];
    const drawGameCount = normalizedDrawGames.reduce((sum: number, line: any) => (
      sum + toRequiredNumberOrZero(line?.count)
    ), 0);
    const drawGameAmount = normalizedDrawGames.reduce((sum: number, line: any) => (
      sum + toRequiredNumberOrZero(line?.amount)
    ), 0);

    const nextParsedData = {
      ...parsedData,
      include_inventory_report: includeInventoryReport,
      location_key: locationKey,
      shift_date: shiftDate,
      shift_time: shiftTime,
      report_date: shiftDate,
      report_time: shiftTime,
      final_json: {
        ...nestedShiftFinalJson,
        report_type: reportType,
        include_inventory_report: includeInventoryReport,
        location_key: locationKey,
        shift_date: shiftDate,
        shift_time: shiftTime,
        report_date: shiftDate,
        report_time: shiftTime,
        sales: {
          ...(finalJson?.sales ?? {}),
          report_type: 'vending_sales',
          lines: normalizedSalesLines,
          scratcher_lines: normalizedSalesLines,
          draw_games: normalizedDrawGames,
          summary: {
            ...(finalJson?.sales?.summary ?? {}),
            draw_game_count: drawGameCount,
            draw_game_amount: drawGameAmount,
          },
        },
        inventory: {
          ...(finalJson?.inventory ?? {}),
          report_type: 'vending_inventory',
          lines: normalizedInventoryLines,
          summary: finalJson?.inventory?.summary ?? {},
        },
      },
    };

    return {
      action: data?.action,
      notes: data?.notes,
      confidence_score: Number(data?.confidence_score ?? 0),
      include_inventory_report: includeInventoryReport,
      location_key: locationKey,
      shift_date: shiftDate,
      shift_time: shiftTime,
      report_date: shiftDate,
      report_time: shiftTime,
      parsed_data: nextParsedData,
      receipt_audit_queue_id: queueId,
    };
  }

  if (reportType === 'vending_sales' || reportType === 'vending_inventory') {
    const parsedData = data?.parsed_data ?? {};
    const finalJson = parsedData?.final_json ?? {};

    const candidateLines = data?.parsed_data?.final_json?.lines;
    const lines = Array.isArray(candidateLines) ? candidateLines : [];

    const normalizedLines = lines.map((line: any) => {
      const base = {
        bin: toOptionalNumberOrNull(line?.bin),
        game: toOptionalNumberOrNull(line?.game),
      };

      if (reportType === 'vending_inventory') {
        const selectedPacks = normalizeSelectedPacks(line);
        return {
          ...base,
          selected_pack_id: getPrimarySelectedPackId(line),
          quantity: toOptionalNumberOrNull(line?.quantity),
          value: toRequiredNumberOrZero(line?.value),
          required_pack_count: toOptionalNumberOrNull(line?.required_pack_count),
          pack_capacity: getPackCapacity(line),
          selected_packs: selectedPacks,
        };
      }

      return {
        ...base,
        count: toVendingSalesCount(line?.count, line?.amount),
        amount: toRequiredNumberOrZero(line?.amount),
      };
    }).filter(reportType === 'vending_inventory'
      ? hasMeaningfulVendingInventoryLine
      : hasMeaningfulVendingSalesLine);

    const summary = data?.parsed_data?.final_json?.summary ?? {};
    const draw_game_count = toRequiredNumberOrZero(summary?.draw_game_count);
    const draw_game_amount = toRequiredNumberOrZero(summary?.draw_game_amount);
    const shiftDate = data?.shift_date ?? parsedData.shift_date;
    const shiftTime = reportType === 'vending_sales'
      ? VENDING_SALES_END_OF_DAY_TIME
      : (data?.shift_start_time || parsedData.shift_time || parsedData.time);
    const locationKey = data?.location_key
      ?? parsedData.location_key
      ?? finalJson.location_key
      ?? '';

    const nextParsedData = {
      ...parsedData,
      location_key: locationKey,
      shift_date: shiftDate,
      shift_time: shiftTime,
      final_json: {
        ...finalJson,
        report_type: reportType,
        location_key: locationKey,
        lines: normalizedLines,
        draw_games: reportType === 'vending_sales' && Array.isArray(finalJson?.draw_games)
          ? finalJson.draw_games.map((line: any) => ({
            ...line,
            count: toVendingSalesCount(line?.count, line?.amount),
            amount: toRequiredNumberOrZero(line?.amount),
          }))
          : finalJson?.draw_games,
        summary: {
          ...(finalJson?.summary ?? {}),
          draw_game_count,
          draw_game_amount,
        },
      },
    };

    const baseRequest: AuditUpdateRequest = {
      action: data?.action,
      notes: data?.notes,
      confidence_score: Number(data?.confidence_score ?? 0),
      location_key: locationKey,
      shift_date: shiftDate,
      shift_time: shiftTime,
      parsed_data: nextParsedData,
    };

    if (reportType === 'vending_sales' && data?.action !== 'reject') {
      const activityLines = normalizedLines.map((line: any) => ({
        slot_id: line.bin,
        game_id: line.game,
        qty_sold: toOptionalNumberOrNull(line.count),
        amount: toRequiredNumberOrZero(line.amount),
      }));
      baseRequest.location_key = locationKey;
      baseRequest.report_date = shiftDate;
      baseRequest.report_time = shiftTime;
      baseRequest.lines = [
        ...activityLines.map((line: any) => ({
          line_type: 'scratcher',
          bin: line.slot_id,
          game_id: line.game_id === null || line.game_id === undefined ? null : String(line.game_id),
          count: toVendingSalesCount(line.qty_sold, line.amount),
          amount: toRequiredNumberOrZero(line.amount),
        })),
        {
          line_type: 'draw_game',
          item_name: 'DRAW GAME',
          count: draw_game_count,
          amount: draw_game_amount,
        },
      ];
    }

    if ((reportType === 'vending_sales' || reportType === 'vending_inventory') && queueId) {
      baseRequest.receipt_audit_queue_id = queueId;
    }

    return baseRequest;
  }

  const parsedData = data?.parsed_data ?? {};
  const finalJson = parsedData?.final_json ?? {};

  const nextParsedData = {
    ...parsedData,
    shift_date: data?.shift_date || parsedData.shift_date || finalJson.shift_date,
    final_json: {
      ...finalJson,
      report_type: finalJson.report_type || reportType,
    },
  };

  return {
    action: data?.action,
    notes: data?.notes,
    confidence_score: Number(data?.confidence_score),
    parsed_data: nextParsedData,
  };
};
